/**
 * La prosa del asesor llega como TEXTO. El arreglo de raíz es que NO emita
 * Markdown (el prompt de la API se lo prohíbe desde el tramo 3: «texto plano,
 * sin asteriscos, sin listas, sin títulos»). Esto es la RED DE SEGURIDAD para
 * cuando el modelo se despista: nadie debe leer un `**` ni un `###` crudo en
 * mitad de una frase (cazado contra la API real, 2026-09-18).
 *
 * Vive en el SISTEMA porque el mismo turno se lee en dos sitios: el Despacho
 * se lo enseña al directivo y el inspector del Studio se lo enseña al equipo
 * — y el inspector promete «qué recibió EXACTAMENTE el asesor», así que no
 * puede pintarlo de otra manera. (`apps/app/src/features/conversacion/prosa.ts`
 * es el original y pasa a importarse de aquí.)
 *
 * Qué traduce, y nada más: el énfasis fuerte (`**así**`), el suave
 * (`*así*`), el corte de párrafos, y las marcas de LISTA (`- `, `1. `) y de
 * TÍTULO (`### `) — que no pintan listas ni títulos: se limpian y cada línea
 * pasa a ser un párrafo corto, que es como habla el asesor. Nada de HTML
 * ni enlaces. Lo que el asesor dice se pinta, no se interpreta: cuando la
 * respuesta traiga estructura de verdad (figuras, fuentes citadas), vendrá
 * por el contrato, no por un parser.
 *
 * LA EXCEPCIÓN, una tabla (decisión de Martí, 2026-10-27): el prompt le
 * permite al asesor UNA tabla GFM cuando hay cuatro filas o más de lo
 * mismo. `bloques` la reconoce y la devuelve como bloque `tabla`, que el
 * Despacho y el inspector pintan con la pieza `sk-prosetable`. Si no
 * cuadra (sin línea de separación, columnas que no casan), NO se adivina:
 * sus líneas se pintan como texto, tal cual llegaron.
 *
 * Y UNA LISTA NUMERADA (decisión de Martí, 2026-10-30): cuando el método
 * enumera pasos u opciones (tres o más; nunca para cifras), el prompt le
 * permite UNA lista «1. …». `bloques` reconoce dos líneas numeradas
 * seguidas o más (aunque las separe una línea en blanco) y las devuelve
 * como bloque `lista`, que se pinta con la pieza `sk-proselist`. Una línea
 * numerada sola no es una lista: se queda como párrafo sin la marca, como
 * siempre. Las viñetas (`- `) siguen sin pintarse como lista.
 */
export interface Trozo { texto: string; fuerte: boolean; cursiva: boolean }

/** `- `, `* `, `+ `, `1. `, `2) ` al principio de una línea. */
const LISTA = /^\s*(?:[-*+]|\d{1,3}[.)])\s+/;
/** `#`…`######` al principio de una línea. */
const TITULO = /^\s*#{1,6}\s+/;
/** `**fuerte**` primero, para que el escáner no lo confunda con dos cursivas. */
const MARCAS = /\*\*([^*]+)\*\*|\*([^*\n]+)\*/g;

/**
 * Parte un párrafo en trozos, marcando lo que va entre `**` (fuerte) y entre
 * `*` (suave). Una marca sin pareja NO emborrona lo que viene detrás: se
 * queda como texto, tal cual la escribió el modelo.
 */
export function trozos(parrafo: string): ReadonlyArray<Trozo> {
  const out: Trozo[] = [];
  const llano = (texto: string): void => { if (texto !== "") out.push({ texto, fuerte: false, cursiva: false }); };
  let i = 0;
  MARCAS.lastIndex = 0;
  for (let m = MARCAS.exec(parrafo); m !== null; m = MARCAS.exec(parrafo)) {
    llano(parrafo.slice(i, m.index));
    if (m[1] !== undefined) out.push({ texto: m[1], fuerte: true, cursiva: false });
    else if (m[2] !== undefined) out.push({ texto: m[2], fuerte: false, cursiva: true });
    i = m.index + m[0].length;
  }
  llano(parrafo.slice(i));
  return out.length === 0 ? [{ texto: parrafo, fuerte: false, cursiva: false }] : out;
}

/**
 * Los párrafos de un turno. Separa la línea en blanco; un salto suelto es el
 * modelo envolviendo a mano, y partir por él deshacía el ritmo de la prosa
 * del Despacho (margen entre párrafos donde no lo había) y desparejaba un
 * `**…**` que cruzara el salto. La EXCEPCIÓN son las líneas marcadas como
 * lista o título: esas sí valen por sí solas — sin marca y como párrafo
 * corto, que es lo que el asesor debería haber escrito de entrada.
 */
export function parrafos(texto: string): ReadonlyArray<string> {
  const out: string[] = [];
  for (const bloque of texto.split(/\n\s*\n/)) {
    let junta: string[] = [];
    const cerrar = (): void => { const p = junta.join(" ").trim(); if (p !== "") out.push(p); junta = []; };
    for (const linea of bloque.split("\n")) {
      if (LISTA.test(linea) || TITULO.test(linea)) {
        cerrar();
        const limpia = linea.replace(TITULO, "").replace(LISTA, "").trim();
        if (limpia !== "") out.push(limpia);
      } else junta.push(linea.trim());
    }
    cerrar();
  }
  return out;
}

/* ───────────────────────── Tablas (2026-10-27) ───────────────────────── */

/** Un bloque del turno: un párrafo, una tabla completa, una tabla que aún se está escribiendo o una lista numerada. */
export type Bloque =
  | { tipo: "parrafo"; texto: string }
  | { tipo: "tabla"; tabla: TablaProsa }
  | { tipo: "tabla-en-curso" }
  | { tipo: "lista"; items: ReadonlyArray<string> };

export interface TablaProsa {
  cabecera: ReadonlyArray<string>;
  filas: ReadonlyArray<ReadonlyArray<string>>;
  /** Por columna: ¿son cifras? (mono tabular, a la derecha). La primera columna nunca: es el nombre de la fila. */
  cifras: ReadonlyArray<boolean>;
}

/** Una línea de tabla GFM: empieza por `|` (el prompt pide barras a los dos lados). */
const FILA = /^\s*\|/;
/** Una celda de la línea de separación: `---`, `:--`, `--:`, `:-:`. */
const SEPARA = /^:?-+:?$/;
/** Una celda que es una cifra: «61.250 €», «-1,2 %», «+3,6 pp», «48 días», «12,5 k€»; «—» vacía. */
const CIFRA = /^[-−+]?\d[\d.,]*(?:\s?(?:%|€|k€|M€|mil|millones|pp|p\.\s?p\.|puntos?|h|días?))?$/u;
const VACIA = /^(?:|—|–|-)$/;

/* ───────────────────── Lista numerada (2026-10-30) ───────────────────── */

/** Un ítem numerado: «1. », «2) » (hasta dos cifras: «2025. Aquel año…» es prosa). */
const NUMERADO = /^\s*\d{1,2}[.)]\s+/;
/** La continuación de un ítem: una línea sangrada (dos espacios o más) que no es otra marca. */
const SANGRADA = /^\s{2,}\S/;
/** En streaming, la marca sola al final («2.», «3») aún sin su texto: no se enseña cruda. */
const MARCA_SOLA = /^\s*\d{1,2}[.)]?\s*$/;
/** Entre dos ítems numerados, la línea en blanco no corta la lista (el modelo a veces los separa). */
const HUECO_EN_LISTA = /^(\s*\d{1,2}[.)]\s.*)\n(?:[ \t]*\n)+(?=\s*\d{1,2}[.)]\s)/gm;

/** Las celdas de una línea: fuera las barras de los bordes; `\|` es una barra escrita, no un corte. */
function celdas(linea: string): string[] {
  let t = linea.trim();
  if (t.startsWith("|")) t = t.slice(1);
  if (t.endsWith("|") && !t.endsWith("\\|")) t = t.slice(0, -1);
  return t.split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, "|"));
}

/** Las líneas de una tabla → la tabla, o `null` si no cuadra (y entonces se pinta como texto). */
export function tabla(lineas: ReadonlyArray<string>): TablaProsa | null {
  if (lineas.length < 3) return null;
  const cabecera = celdas(lineas[0]!);
  const n = cabecera.length;
  if (n < 2) return null;
  const sep = celdas(lineas[1]!);
  if (sep.length !== n || !sep.every((c) => SEPARA.test(c))) return null;
  const filas: string[][] = [];
  for (const l of lineas.slice(2)) {
    const f = celdas(l);
    // de más: no se recorta ningún dato a escondidas; de menos, se rellena (como GFM)
    if (f.length > n) return null;
    filas.push([...f, ...Array<string>(n - f.length).fill("")]);
  }
  const cifras = cabecera.map((_, j) => {
    if (j === 0) return false;
    const llenas = filas.map((f) => f[j]!).filter((c) => !VACIA.test(c));
    const derecha = /^-+:$/.test(sep[j]!);
    return llenas.length > 0 && (derecha || llenas.every((c) => CIFRA.test(c)));
  });
  return { cabecera, filas: igualarDecimales(filas, cifras), cifras };
}

/** Marcas de énfasis que pueden rodear una cifra en una celda («**61.250 €**»). */
const ENFASIS = /[*_]/g;
/** El número de una celda, en formato español: miles con «.» (o sin agrupar), decimales con «,». */
const NUMERO_ES = /\d{1,3}(?:\.\d{3})+(?:,\d+)?|\d+(?:,\d+)?/;
/** El primer tramo de dígitos de la celda (con sus «.» y «,»): ahí está la cifra. */
const TRAMO = /\d[\d.,]*/;

/**
 * LOS MISMOS DECIMALES POR COLUMNA (feedback de Martí sobre G-95,
 * 2026-11-03): el modelo escribe «61.950,50 €» en una fila y «84.200 €» en
 * otra, y alineadas a la derecha las comas no casan. En cada columna de
 * cifras, todas llevan los decimales de la que más traiga, rellenando con
 * ceros («84.200 €» → «84.200,00 €»). Una columna sin decimales queda igual.
 * Solo se tocan las celdas que son una cifra (énfasis aparte) en formato
 * español; «—», vacías y texto, intactas; la primera columna, nunca. Es
 * solo al pintar: el comprobador de cifras lee el texto crudo en el servidor.
 */
export function igualarDecimales(
  filas: ReadonlyArray<ReadonlyArray<string>>,
  cifras: ReadonlyArray<boolean>,
): string[][] {
  const out = filas.map((f) => [...f]);
  cifras.forEach((esCifra, j) => {
    if (!esCifra || j === 0) return;
    // por celda: el tramo de dígitos, si la celda es una cifra en formato español
    const tramos = out.map((f) => {
      const c = f[j] ?? "";
      if (!CIFRA.test(c.replace(ENFASIS, "").trim())) return null;
      const t = TRAMO.exec(c);
      if (t === null) return null;
      const m = NUMERO_ES.exec(t[0]);
      return m !== null && m[0] === t[0] ? { index: t.index, numero: t[0] } : null;
    });
    const decimales = (n: string): number => (n.includes(",") ? n.length - n.indexOf(",") - 1 : 0);
    const max = Math.max(0, ...tramos.map((t) => (t === null ? 0 : decimales(t.numero))));
    if (max === 0) return;
    tramos.forEach((t, i) => {
      if (t === null) return;
      const faltan = max - decimales(t.numero);
      if (faltan === 0) return;
      const relleno = `${t.numero}${t.numero.includes(",") ? "" : ","}${"0".repeat(faltan)}`;
      const c = out[i]![j]!;
      out[i]![j] = c.slice(0, t.index) + relleno + c.slice(t.index + t.numero.length);
    });
  });
  return out;
}

/**
 * Los bloques de un turno: párrafos (con las mismas reglas que `parrafos`)
 * y, donde haya un grupo de líneas que empiezan por `|`, una tabla.
 *
 * `enCurso` = la respuesta aún se está escribiendo. Una tabla que es lo
 * ÚLTIMO del texto puede tener más filas por llegar (o media línea): no se
 * enseña a medias ni como texto con barras — sale `tabla-en-curso` («preparando
 * la tabla…»). En cuanto llega texto detrás (el párrafo siguiente) o
 * termina la respuesta, se pinta entera. Una sola regla, sin temporizadores.
 *
 * La LISTA numerada, en cambio, se pinta según llega (2026-10-30): un ítem
 * a medias se lee como un párrafo a medias, que es lo que el Despacho ya
 * enseña al escribir; lo único que no se enseña es la marca cruda, así que
 * la lista se pinta con su pieza desde el primer ítem y una marca sola al
 * final («3.» sin texto aún) se calla hasta que llega su texto. En curso,
 * un único ítem al final ya va en la pieza (lo normal es que sigan más);
 * terminada, una línea numerada sola vuelve a ser un párrafo.
 */
export function bloques(texto: string, { enCurso = false }: { enCurso?: boolean } = {}): ReadonlyArray<Bloque> {
  const out: Bloque[] = [];
  let crudo = texto.replace(/\r\n?/g, "\n").replace(HUECO_EN_LISTA, "$1\n");
  if (enCurso) {
    // la marca sola al final, tras una línea en blanco o un ítem: aún sin texto, no se enseña
    const ls = crudo.replace(/\s+$/, "").split("\n");
    const previa = ls.length > 1 ? ls[ls.length - 2]! : "";
    if (MARCA_SOLA.test(ls[ls.length - 1]!) && (previa.trim() === "" || NUMERADO.test(previa))) crudo = ls.slice(0, -1).join("\n");
  }
  const porBloque = crudo.split(/\n\s*\n/);
  // ¿qué bloque es el último con algo escrito? (una tabla al final puede venir seguida de líneas en blanco)
  let ultimo = porBloque.length - 1;
  while (ultimo > 0 && porBloque[ultimo]!.trim() === "") ultimo--;
  porBloque.forEach((bloque, b) => {
    const lineas = bloque.split("\n");
    let junta: string[] = [];
    const cerrar = (): void => { const p = junta.join(" ").trim(); if (p !== "") out.push({ tipo: "parrafo", texto: p }); junta = []; };
    for (let i = 0; i < lineas.length; i++) {
      const linea = lineas[i]!;
      if (FILA.test(linea)) {
        cerrar();
        const grupo: string[] = [];
        while (i < lineas.length && FILA.test(lineas[i]!)) grupo.push(lineas[i++]!);
        i--;
        const alFinal = b === ultimo && lineas.slice(i + 1).every((l) => l.trim() === "");
        if (enCurso && alFinal) { out.push({ tipo: "tabla-en-curso" }); continue; }
        const t = tabla(grupo);
        if (t !== null) out.push({ tipo: "tabla", tabla: t });
        else for (const g of grupo) { const p = g.trim(); if (p !== "") out.push({ tipo: "parrafo", texto: p }); }
      } else if (NUMERADO.test(linea)) {
        cerrar();
        const items: string[] = [];
        while (i < lineas.length) {
          const l = lineas[i]!;
          if (NUMERADO.test(l)) items.push(l.replace(NUMERADO, "").trim());
          else if (SANGRADA.test(l) && !LISTA.test(l) && !TITULO.test(l) && !FILA.test(l)) items[items.length - 1] = `${items[items.length - 1]!} ${l.trim()}`.trim();
          else break;
          i++;
        }
        i--;
        const llenos = items.filter((t) => t !== "");
        const alFinal = b === ultimo && lineas.slice(i + 1).every((l) => l.trim() === "");
        if (llenos.length >= 2 || (enCurso && alFinal && llenos.length === 1)) out.push({ tipo: "lista", items: llenos });
        else for (const t of llenos) out.push({ tipo: "parrafo", texto: t });
      } else if (LISTA.test(linea) || TITULO.test(linea)) {
        cerrar();
        const limpia = linea.replace(TITULO, "").replace(LISTA, "").trim();
        if (limpia !== "") out.push({ tipo: "parrafo", texto: limpia });
      } else junta.push(linea.trim());
    }
    cerrar();
  });
  return out;
}
